
import { defineComponent } from "vue";
import { getCurrentUser } from "@/utils/index";

function formatRelativeDate(value: string) {
  if (!value) return "Not activated yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diffMs = Date.now() - date.getTime();
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days <= 0) return "Created today";
  if (days === 1) return "Created 1 day ago";
  if (days < 30) return `Created ${days} days ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function truncateUsid(usid: string) {
  const value = String(usid || "");
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

function accountInitials(accountName: string, fallback: string) {
  const words = String(accountName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0] || ""}${words[1][0] || ""}`.toUpperCase();
  }
  if (words.length === 1 && words[0].length >= 2) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return String(fallback || "?").slice(0, 2).toUpperCase();
}

function shortLocationId(locationId: string) {
  const value = String(locationId || "");
  if (!value) return "—";
  if (value.length <= 10) return value;
  return `…${value.slice(-6)}`;
}

export default defineComponent({
  name: "ai247-instance-rail",
  props: {
    instances: {
      type: Array,
      default: () => []
    },
    pendingActivationIds: {
      type: Array,
      default: () => []
    },
    activeInstanceKey: {
      type: String,
      default: ""
    },
    instanceFilter: {
      type: String,
      default: "all"
    },
    selectedSubscriptionPlan: {
      type: String,
      default: ""
    }
  },
  emits: ["select", "filter-change", "activate-another", "collapse"],
  data() {
    return {
      filterOptions: [
        { value: "all", label: "All" },
        { value: "active", label: "Active" },
        { value: "pending", label: "Pending" }
      ]
    };
  },
  computed: {
    avatarUrl(): string {
      const user = getCurrentUser();
      return user?.avatar || "";
    },
    unifiedRows(): any[] {
      const rows = (this.instances as any[]).map((instance) => {
        const accountName = String(instance.accountName || "").trim();
        return {
          key: String(instance.usid),
          title: accountName || truncateUsid(instance.usid),
          subLabel: formatRelativeDate(instance.createdAt),
          statusLabel: this.formatStatus(instance.status),
          meta: accountName ? truncateUsid(instance.usid) : shortLocationId(instance.locationId),
          filterBucket: this.statusBucket(instance.status),
          initials: accountInitials(accountName, instance.usid)
        };
      });

      for (const activationId of this.pendingActivationIds as string[]) {
        rows.push({
          key: String(activationId),
          title: "Activation slot",
          subLabel: "Not activated yet",
          statusLabel: "Not activated",
          meta: truncateUsid(activationId),
          filterBucket: "pending",
          initials: "AI"
        });
      }

      return rows;
    },
    visibleRows(): any[] {
      if (this.instanceFilter === "all") return this.unifiedRows;
      return this.unifiedRows.filter((row) => row.filterBucket === this.instanceFilter);
    },
    totalCount(): number {
      return this.unifiedRows.length;
    }
  },
  methods: {
    formatStatus(status: string) {
      if (status === "suspended") return "Disconnected";
      if (status === "pending_application") return "Pending";
      if (status === "expired") return "Expired";
      if (status === "active") return "Active";
      if (!status) return "Unknown";
      return String(status).replace(/_/g, " ");
    },
    statusBucket(status: string) {
      if (status === "active") return "active";
      if (status === "pending_application" || status === "expired" || status === "suspended") {
        return "pending";
      }
      return "pending";
    }
  }
});
